import { useCallback, useEffect, useRef, useState } from "react";

const TEN_SECONDS = 10000;

export function useAppWebSockets(userIdle: boolean) {
    const [multiWebSocket, setMultiWebSocket] = useState<WebSocket>();
    const [ortexWebSocket, setOrtexWebSocket] = useState<WebSocket>();

    // Timeouts for reconnect logic - use refs to persist across renders
    const multiSocketTimeoutRef = useRef<NodeJS.Timeout>();
    const ortexSocketTimeoutRef = useRef<NodeJS.Timeout>();

    // Send hello messages
    const sendHelloMulti = useCallback(() => multiWebSocket?.send(JSON.stringify({ type: "hello" })), [multiWebSocket]);
    const sendHelloOrtex = useCallback(() => ortexWebSocket?.send(JSON.stringify({ type: "hello" })), [ortexWebSocket]);

    // Close sockets
    const closeMultiSocket = useCallback(() => multiWebSocket?.close(), [multiWebSocket]);
    const closeOrtexSocket = useCallback(() => ortexWebSocket?.close(), [ortexWebSocket]);

    // Reset sockets (reconnect)
    const resetMultiSocket = useCallback(() => {
        if (multiSocketTimeoutRef.current) {
            clearTimeout(multiSocketTimeoutRef.current);
        }
        multiSocketTimeoutRef.current = setTimeout(() => setMultiWebSocket(new WebSocket(`${process.env.NEXT_PUBLIC_MULTI_WS_URL}/stock_price/MULTI`)), TEN_SECONDS);
    }, []);
    const resetOrtexSocket = useCallback(() => {
        if (ortexSocketTimeoutRef.current) {
            clearTimeout(ortexSocketTimeoutRef.current);
        }
        ortexSocketTimeoutRef.current = setTimeout(() => setOrtexWebSocket(new WebSocket(`${process.env.NEXT_PUBLIC_ORTEX_WS_URL}/stock_price`)), TEN_SECONDS);
    }, []);

    // Initialize WebSockets when user is not idle
    useEffect(() => {
        if (!userIdle) {
            setMultiWebSocket(new WebSocket(`${process.env.NEXT_PUBLIC_MULTI_WS_URL}/stock_price/MULTI`));
            setOrtexWebSocket(new WebSocket(`${process.env.NEXT_PUBLIC_ORTEX_WS_URL}/stock_price`));
        }
    }, [userIdle]);

    // Set up WebSocket event listeners
    useEffect(() => {
        if (multiWebSocket) {
            multiWebSocket.readyState === 1 ? sendHelloMulti() : multiWebSocket.addEventListener("open", sendHelloMulti);
            multiWebSocket.addEventListener("error", closeMultiSocket);
            multiWebSocket.addEventListener("close", resetMultiSocket);
        }
        return () => {
            if (multiWebSocket) {
                multiWebSocket.removeEventListener("open", sendHelloMulti);
                multiWebSocket.removeEventListener("error", closeMultiSocket);
                multiWebSocket.removeEventListener("close", resetMultiSocket);
            }
        };
    }, [multiWebSocket, sendHelloMulti, closeMultiSocket, resetMultiSocket]);

    useEffect(() => {
        if (ortexWebSocket) {
            ortexWebSocket.readyState === 1 ? sendHelloOrtex() : ortexWebSocket.addEventListener("open", sendHelloOrtex);
            ortexWebSocket.addEventListener("error", closeOrtexSocket);
            ortexWebSocket.addEventListener("close", resetOrtexSocket);
        }
        return () => {
            if (ortexWebSocket) {
                ortexWebSocket.removeEventListener("open", sendHelloOrtex);
                ortexWebSocket.removeEventListener("error", closeOrtexSocket);
                ortexWebSocket.removeEventListener("close", resetOrtexSocket);
            }
        };
    }, [ortexWebSocket, sendHelloOrtex, closeOrtexSocket, resetOrtexSocket]);

    // Close sockets when user becomes idle
    useEffect(() => {
        if (userIdle) {
            if (multiWebSocket?.readyState === 1) {
                multiWebSocket.removeEventListener("close", resetMultiSocket);
                closeMultiSocket();
            }
            if (ortexWebSocket?.readyState === 1) {
                ortexWebSocket.removeEventListener("close", resetOrtexSocket);
                closeOrtexSocket();
            }
        }
    }, [userIdle, multiWebSocket, ortexWebSocket, resetMultiSocket, resetOrtexSocket, closeMultiSocket, closeOrtexSocket]);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (multiSocketTimeoutRef.current) {
                clearTimeout(multiSocketTimeoutRef.current);
            }
            if (ortexSocketTimeoutRef.current) {
                clearTimeout(ortexSocketTimeoutRef.current);
            }
        };
    }, []);

    return { multiWebSocket, ortexWebSocket };
}
